import { memo, useDeferredValue, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, ArrowLeftRight, ArrowUp, Check, ChevronDown, ChevronRight, Dices, Loader2, PencilLine, Plus, Sparkles, Wrench } from 'lucide-react'
import { toast } from 'sonner'
import { imageGenerationErrorMessage } from '@/components/post-creator/imageGenerationJob'
import { useQuery } from '@tanstack/react-query'
import { brandingToCssVars } from '@feed-widget/branding'
import { PrepackCardView, PrepacksSurfaceView } from '@feed-widget/surfaces/PrepacksPresentation'
import { prepacksApi } from '@/services/api'
import { bannerFramingWash, builderShowsJerseyNumbers, DEFAULT_SAMPLE_SPORT, PREVIEW_TEAM_ID, SAMPLE_BANNER_VALUE, sampleBannerValue, sampleFromSaved, SAMPLE_PLAYER_NUMBERS, savedSample, TEMPLATE_SAMPLE_FEED, TEMPLATE_SAMPLE_PACK, buildSamplePack, sampleIsDefault, sampleTeamBetLegs, toPublicPrepack, withPreviewLogo } from './prepackCardModel'
import { TeamPicker } from './TemplateTeamSelect'
import { SampleContentPanel } from './SampleContentPanel'
import { FIELD, PRIMARY_BUTTON, QUIET_BUTTON, SEGMENT, SELECTED_TILE, THIN_SCROLL, UNSELECTED_TILE, segmentButton } from './templateBuilderStyles'
import { usePrepackPreview } from './prepackPreviewContext'
import PrepackChoiceCard from './PrepackChoiceCard'
import { NativeAssetControl } from '../native-sportsbook/NativeAssetControl'
import { usePictureMediaAccess } from './usePictureMediaAccess'
import { scheduleBannerChanges } from './prepackScheduleModel'
import { scheduleQueryKeys } from './prepackScheduleQueries'
import BannerFraming from './BannerFraming'
import { usePaintBanner } from './usePaintBanner'
import { useCardAspect, useHeaderAspect } from './useCardAspect'
import { ADDED_CSS_KEYS, BANNER_FILL_CARD, BANNER_FILL_HEADER, BANNER_FOOTER_TINTED, BANNER_FOOTER_TRANSPARENT, BANNER_IMAGE, BANNER_IMAGE_VARIABLE, bannerFillOf, bannerFooterOf, bannerImageProblem, bannerImageValue, bannerTitleOf, BUILDER_BANNERS, BUILDER_DEFAULTS, BUILDER_FEATURES, BUILDER_FONT_FACES, BUILDER_FONT_FAMILIES, BUILDER_FONTS, BUILDER_LAYOUTS, BUILDER_MODES, builderVariables, compileBuilderTemplate, counterIcon, defaultBannerPosition, EMPTY_ADDED_CSS, isBannerImageUrl, isHexColor, keepRetiredOptions, layoutHasOwnFont, MAX_BANNER_PROMPT_RUNES, MIN_BANNER_SCALE, normalizeBannerImage, normalizeBuilderChoices, SHELL_FIELDS, TITLE_ALIGNMENT_CENTER, TITLE_ALIGNMENT_LEFT, TITLE_PLACEMENT_BELOW, TITLE_PLACEMENT_NONE, TITLE_PLACEMENT_OVER, unlockCssOnlyEdits } from '../../../../../shared/pkg/prepacks/templateBuilder.js'
import { LEG_LAYOUT_CSS, injectPackTokens, resolveBuiltinCssUrls, teamLogoUrl } from '../../../../../shared/pkg/prepacks/templateRendering.js'

const LEADING_HASH_PATTERN = /^#/
const SHORT_HEX_DIGITS_PATTERN = /^[0-9a-f]{3}$/i
const FONT_FACE_RULE_PATTERN = /@font-face\{[^}]*\}/g
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i
const WHITESPACE_RUN_PATTERN = /\s+/g

// Three groups of choices, in the order that usually makes sense. They are
// tabs, not a sequence: any of them can be opened at any time.
const TABS = [
  { key: 'layout', label: 'Layout', title: 'Layout' },
  { key: 'features', label: 'Features', title: 'What the card shows' },
  { key: 'colors', label: 'Colors', title: 'Colors' },
  { key: 'advanced', label: 'Advanced', title: 'Advanced', icon: Wrench },
]
// The three choice tabs are the suggested path; Advanced is reached on purpose.
const LAST_CHOICE_TAB = 2
const ADVANCED_TAB = TABS.length - 1
const MAX_PROMPT = 300
// "Describe the card" (#996): hidden until its answers are worth applying. Flip this to bring the bar back on both
// layouts; its unit tests are the it.skip/describe.skip cases tagged #996 in PrepacksConfigurationPage.test.jsx.
const DESCRIBE_CARD_SHOWN = false
// Example prompts. Three are shown at a time, drawn at random, so operators
// see the range of what can be asked without a wall of chips.
const START_SUGGESTIONS = [
  'Packers colors, bold header', 'Black and gold with a gradient banner', 'Simple card without icons', 'Lakers theme, compact',
  'Editorial look in red and white', 'Navy and orange hero card', 'Minimal card, no bet count', 'Spotlight card in Chiefs colors',
  'Soft rows, teal and white', 'Split banner, purple and gold', 'Dark green and cream, show the game', 'Yankees colors with odds per selection',
  'Cowboys card with logos and jerseys', 'Stadium at night behind the title', 'Lakers colors with jerseys',
]
const REFINE_SUGGESTIONS = [
  'Swap the two colors', 'Add a split banner', 'Make it more compact', 'Hide the bet count', 'Show odds per selection',
  'Use a gradient banner', 'Remove the banner', 'Try the editorial layout', 'Make the accent brighter', 'Show the game on each selection',
  'Switch to the Cowboys', 'Show jerseys', 'Put a rainy pitch behind the title', 'No team',
]
// Scenes for a generated banner, one at a time behind the dice button; the pack's game is added when the picture is made.
const BANNER_SCENE_SUGGESTIONS = [
  'Stadium at night under floodlights', 'Packed stands with confetti falling', 'Rain on an empty pitch at dusk', 'Court-side haze and arena lights',
  'Ice rink glare in blue and white', 'Tailgate at golden hour', 'Abstract speed streaks in the team colors', 'Fireworks over a stadium bowl',
]
// What a described design does to "Allow AI pictures" (the design answer's ai_pictures).
const AI_PICTURES_ON = 'on'
const AI_PICTURES_OFF = 'off'
const AI_PICTURES_KEEP = 'keep'
const AI_PICTURES = [AI_PICTURES_ON, AI_PICTURES_OFF, AI_PICTURES_KEEP]
/** The picture the preview shows: the fixed one, else a tried scene, else the stand-in where packs paint their own, in the shape they are painted in (#979); nothing for the other banners. */
function previewBannerValue(choices, tried) {
  if (choices.banner !== BANNER_IMAGE) return undefined
  const image = normalizeBannerImage(choices.image)
  if (image.url) return bannerImageValue(image.url)
  if (tried) return bannerImageValue(tried)
  return image.ai_allowed ? sampleBannerValue({ fill: bannerFillOf(image), layout: choices.layout, title: choices.title }) : undefined
}
/** A painted scene remains visible only while its scene, composition, layout and sample subject still match. */
function triedPictureFor(tried, choices, subject) {
  if (!tried || choices.banner !== BANNER_IMAGE || !isBannerImageUrl(tried.url) || tried.subject !== subject || tried.layout !== choices.layout) return null
  const image = normalizeBannerImage(choices.image)
  const title = bannerTitleOf(choices.title, bannerFillOf(image))
  if (!image.ai_allowed || tried.prompt !== image.prompt || tried.fill !== bannerFillOf(image)
    || tried.title?.placement !== title.placement || tried.title?.alignment !== title.alignment) return null
  return tried.url
}
/** The scene limit counts characters the way the server does (runes), not UTF-16 units: emoji keep their full allowance. */
function clampRunes(value) {
  return Array.from(value).slice(0, MAX_BANNER_PROMPT_RUNES).join('')
}
/** Why the image banner cannot be saved yet, or null. */
function bannerProblemOf(choices) {
  return choices.banner === BANNER_IMAGE ? bannerImageProblem(choices.image) : null
}

/**
 * The image banner's state for a design request: whether AI pictures are allowed, the default scene and the fixed
 * picture (the server tells the model only whether there is one). Nothing without an image banner.
 */
function designImageOf(choices) {
  if (choices.banner !== BANNER_IMAGE) return {}
  const image = normalizeBannerImage(choices.image)
  return { image: { ai_allowed: image.ai_allowed, source: image.source, url: image.url, prompt: image.prompt } }
}

function pickSuggestions(pool, count = 6) {
  const rest = [...pool]
  const picked = []
  while (picked.length < count && rest.length > 0) picked.push(rest.splice(Math.floor(Math.random() * rest.length), 1)[0])
  return picked
}

// The builder's own header row on a phone, which the preview sticks under.
const PHONE_HEADER_HEIGHT = 53

function newTemplateId() {
  return `tpl-${globalThis.crypto?.randomUUID?.() || Date.now()}`
}

/** A color swatch with its hex field. Half-typed values stay in the field and only a complete hex is committed. */
function HexField({ label, value, disabled, onChange }) {
  const id = useId()
  const [text, setText] = useState(value)
  // A new value from outside (swap, reset, a described design) replaces whatever was half-typed.
  const [shown, setShown] = useState(value)
  if (shown !== value) {
    setShown(value)
    setText(value)
  }
  // "fff" and "#abc" are hex too: expand the 3-digit shorthand before judging.
  const expand = (raw) => {
    const digits = raw.trim().replace(LEADING_HASH_PATTERN, '')
    return `#${SHORT_HEX_DIGITS_PATTERN.test(digits) ? [...digits].map((d) => d + d).join('') : digits}`
  }
  const invalid = text.trim() !== '' && !isHexColor(expand(text))
  const commit = (next) => {
    setText(next)
    const candidate = expand(next)
    if (isHexColor(candidate)) onChange(candidate.toLowerCase())
  }
  return (
    <div className="text-xs">
      <label htmlFor={id} className="mb-1.5 block text-[var(--prepacks-text)]">{label}</label>
      <span className="flex items-center gap-2">
        <input type="color" aria-label={`${label} picker`} value={value} disabled={disabled} onChange={(event) => commit(event.target.value)} className="h-8 w-8 shrink-0 cursor-pointer rounded border border-[var(--prepacks-border)] bg-transparent disabled:opacity-60" />
        <input id={id} value={text} disabled={disabled} spellCheck={false} autoComplete="off" maxLength={7} aria-invalid={invalid || undefined} aria-describedby={invalid ? `${id}-hint` : undefined} onChange={(event) => commit(event.target.value)} onBlur={() => setText(value)} className={`${FIELD} h-8 font-mono text-xs uppercase ${invalid ? '!border-[var(--wf-color-danger)]' : ''}`} />
      </span>
      {invalid ? <p id={`${id}-hint`} className="mt-1 text-[var(--wf-color-danger)]">Use a 6-digit hex, like #1A2B3C.</p> : null}
    </div>
  )
}

/** Shows only the children that fit on one line of the available width; the rest are hidden, never scrolled to. */
function FittingRow({ children, fitKey, className }) {
  const ref = useRef(null)
  useLayoutEffect(() => {
    const row = ref.current
    if (!row) return undefined
    const fit = () => {
      const items = [...row.children]
      for (const item of items) item.hidden = false
      // Widths, not positions: the row centers its content, so positions move as items are hidden.
      const gap = Number.parseFloat(getComputedStyle(row).columnGap) || 0
      const limit = row.clientWidth
      let used = 0
      let count = 0
      for (const item of items) {
        used += item.offsetWidth + (count ? gap : 0)
        if (used > limit + 1) break
        count += 1
      }
      for (const [index, item] of items.entries()) item.hidden = index >= count
    }
    fit()
    // Without ResizeObserver (an old WebView) the first measurement stands.
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(fit)
    observer.observe(row)
    return () => observer.disconnect()
    // Only the set of chips changes their widths; a keystroke in the prompt must not force a layout.
  }, [fitKey])
  return <div ref={ref} className={`flex flex-nowrap gap-1.5 overflow-hidden ${className || ''}`}>{children}</div>
}

/**
 * Describe the card in words and the choices fill themselves in. The answer
 * only ever contains what the tabs offer, so the operator can change any of
 * it by hand afterwards; Undo puts back what it replaced.
 */
function DescribeCard({ orgSlug, choices, disabled, codeLocked = false, onApply, onUndo }) {
  const inputId = useId()
  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  // A fresh draw when the builder opens and after every answer, never while the operator is reading them.
  const [suggestions, setSuggestions] = useState(() => pickSuggestions(START_SUGGESTIONS))
  // Only the newest request may change anything: an older one is aborted, and
  // its answer or failure is dropped by the request id check. Unmounting aborts too.
  const requestId = useRef(0)
  const controller = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => () => controller.current?.abort(), [])

  const design = async (text, teamId) => {
    const request = text.trim()
    if (!orgSlug || !request || busy || disabled) return
    const currentRequest = ++requestId.current
    const requestController = new AbortController()
    controller.current?.abort()
    controller.current = requestController
    // Another team for the same words starts again from what the first answer replaced.
    const before = teamId && result ? result.before : choices
    setBusy(true)
    setError('')
    try {
      const answer = await prepacksApi.designTemplate(orgSlug, {
        prompt: request,
        team_id: teamId,
        current: { layout: before.layout, banner: before.banner, font: before.font, features: before.features, options: before.options, palette: before.palette, team: before.team || '', ...designImageOf(before) },
      }, { signal: requestController.signal })
      if (requestId.current !== currentRequest || requestController.signal.aborted) return
      if (!answer?.layout || !Array.isArray(answer.palette)) {
        setError('No design came back. Try describing the colors or layout you want.')
        return
      }
      const filled = onApply(answer, { team: before.team || '', pick: teamId || '' })
      setResult({ ...answer, prompt: request, before, filled })
      setSuggestions(pickSuggestions(REFINE_SUGGESTIONS))
      setPrompt('')
    } catch (failure) {
      if (requestId.current === currentRequest && !requestController.signal.aborted) {
        // AI endpoints answer with an error object ({code, message}); rendering it as text would throw and unmount the page.
        setError(imageGenerationErrorMessage(failure, 'The design could not be generated. Try again, or choose by hand.'))
      }
    } finally {
      if (controller.current === requestController) controller.current = null
      if (requestId.current === currentRequest) {
        setBusy(false)
        // The chip or button that started this is gone or disabled; typing the next change should need no click.
        inputRef.current?.focus()
      }
    }
  }

  const otherTeams = (result?.candidates || []).filter((team) => team.team_id !== result?.team?.team_id)
  // Characters, not UTF-16 units, so an emoji is never cut in half.
  const promptChars = result ? [...result.prompt] : []
  // The team line holds only while the template still has the team the answer gave (or still has none, when it
  // cleared one); a team picked by hand in the Colors tab afterwards would make it wrong.
  let teamLine = ''
  if (result?.team) teamLine = choices.team === result.team.team_id ? `For the ${result.team.team_name}.` : ''
  else if (result?.before?.team && !choices.team) teamLine = 'No team.'

  return (
    <div className="w-full max-w-[620px] space-y-2 text-xs">
      {/* The live regions hold text only, so an answer is announced without the buttons beside it. They stay
          mounted (visually hidden while empty) so their first message is announced too. */}
      <div className="space-y-1.5 text-center">
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          <p role="status" className={result ? '' : 'sr-only'}>
            {busy ? <span className="sr-only">Designing… </span> : null}
            {result ? <span title={result.prompt}>Applied “{promptChars.length > 80 ? `${promptChars.slice(0, 77).join('')}…` : result.prompt}”.</span> : null}
            {teamLine ? <span className="ml-2 text-[var(--prepacks-muted)]">{teamLine}</span> : null}
          </p>
          {result ? <button type="button" disabled={disabled || busy} onClick={() => { onUndo(result.before, result.filled); setResult(null); setSuggestions(pickSuggestions(START_SUGGESTIONS)); inputRef.current?.focus() }} className="min-h-6 text-[var(--prepacks-accent-text)] underline-offset-2 hover:underline disabled:opacity-50">Undo</button> : null}
        </div>
        {result && otherTeams.length > 0 ? (
          <p className="flex flex-wrap items-center justify-center gap-1.5">
            <span className="text-[var(--prepacks-muted)]">Meant another team?</span>
            {otherTeams.map((team) => (
              <button key={team.team_id} type="button" disabled={disabled || busy} onClick={() => void design(result.prompt, team.team_id)} className="min-h-6 rounded border border-[var(--prepacks-border)] px-2 py-1 text-[var(--prepacks-accent-text)] hover:border-[var(--prepacks-accent-text)] disabled:opacity-50">{team.team_name}</button>
            ))}
          </p>
        ) : null}
      </div>
      {error ? <p role="alert" className="text-center text-[var(--wf-color-danger)]">{error}</p> : null}
      {codeLocked ? <p className="text-center text-[var(--prepacks-muted)]">The HTML and CSS were edited by hand, so a description only changes the colors and the team.</p> : null}
      {/* As many examples as fit on one line, whatever the width; never a scrollbar. */}
      <FittingRow fitKey={suggestions} className="justify-center">
        {suggestions.map((suggestion) => (
          <button key={suggestion} type="button" data-suggestion disabled={disabled || busy} onClick={() => { setPrompt(suggestion); inputRef.current?.focus() }} className="shrink-0 whitespace-nowrap rounded-full border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-3 py-1.5 text-xs text-[var(--prepacks-muted)] hover:border-[var(--prepacks-accent-text)] hover:text-[var(--prepacks-accent-text)] disabled:opacity-50">
            {suggestion}
          </button>
        ))}
      </FittingRow>
      <form
        className="flex items-center gap-2 rounded-2xl border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] py-2 pl-4 pr-2 shadow-lg focus-within:border-[var(--wf-color-action)] focus-within:outline focus-within:outline-[3px] focus-within:outline-[color-mix(in_srgb,var(--wf-color-action)_24%,transparent)]"
        onSubmit={(event) => {
          event.preventDefault()
          void design(prompt)
        }}
      >
        <Sparkles className="h-4 w-4 shrink-0 text-[var(--prepacks-accent-text)]" aria-hidden="true" />
        <label htmlFor={inputId} className="sr-only">Describe the card</label>
        {/* The pill shows focus; the input inside it must not draw a second ring. */}
        <input ref={inputRef} id={inputId} name="card-description" autoComplete="off" value={prompt} maxLength={MAX_PROMPT} disabled={disabled} readOnly={busy} aria-busy={busy || undefined} onChange={(event) => setPrompt(event.target.value)}
          placeholder={result ? 'Describe a change…' : 'Describe the card…'}
          className="min-w-0 flex-1 bg-transparent py-2 text-sm text-[var(--prepacks-text)] !outline-none placeholder:text-[var(--prepacks-muted)] disabled:opacity-60" />
        <button type="submit" disabled={disabled || busy || !prompt.trim()} aria-label={busy ? 'Designing…' : 'Design'} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--prepacks-accent)] text-[var(--wf-color-on-action)] transition-colors hover:bg-[var(--prepacks-accent-hover)] disabled:opacity-50">
          {busy ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" aria-hidden="true" /> : <ArrowUp className="h-4 w-4" aria-hidden="true" />}
        </button>
      </form>
    </div>
  )
}

function Tabs({ idPrefix, active, onChange }) {
  const onKeyDown = (event) => {
    const move = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
    if (!move) return
    event.preventDefault()
    onChange((active + move + TABS.length) % TABS.length)
  }
  return (
    <div role="tablist" aria-label="Template settings" onKeyDown={onKeyDown} className="flex shrink-0 gap-1 border-b border-[var(--prepacks-border)] px-5">
      {TABS.map((tab, index) => {
        const selected = index === active
        return (
          // One weight for every tab: a selected tab that turns bold changes width and shifts its neighbors.
          <button key={tab.key} type="button" role="tab" id={`${idPrefix}-tab-${tab.key}`} aria-selected={selected} aria-controls={`${idPrefix}-panel`} tabIndex={selected ? 0 : -1} onClick={() => onChange(index)}
            className={`-mb-px border-b-2 px-3 py-3 text-xs font-medium transition-colors ${selected ? 'border-[var(--prepacks-accent-text)] text-[var(--prepacks-accent-text)]' : 'border-transparent text-[var(--prepacks-muted)] hover:text-[var(--prepacks-text)]'}`}>
            {tab.icon ? <tab.icon className="mr-1 inline h-3.5 w-3.5 align-[-2px]" aria-hidden="true" /> : null}{tab.label}
          </button>
        )
      })}
    </div>
  )
}

// The bundled title fonts, registered once for the page: @font-face inside a shadow root is ignored.
let thumbnailFontLoaded = false
function loadThumbnailFont() {
  if (thumbnailFontLoaded || typeof document === 'undefined') return
  thumbnailFontLoaded = true
  const style = document.createElement('style')
  style.textContent = resolveBuiltinCssUrls(Object.values(BUILDER_FONT_FACES).join(''))
  document.head.append(style)
}
const THUMBNAIL_PACK = { ...TEMPLATE_SAMPLE_PACK, legs: TEMPLATE_SAMPLE_PACK.legs.slice(0, 2) }

/**
 * A small, real card in one layout with the current banner, features and
 * colors, including the frame and button the widget draws around the artwork.
 * It is drawn into a shadow root, not an iframe: nine extra documents made
 * this panel slow to paint and scroll.
 */
function LayoutThumbnail({ layout, banner, font, features, options, palette, overrides, mode }) {
  const hostRef = useRef(null)
  // Markup and CSS change on layout, banner, font and feature choices; the colors
  // (the most frequent change, a picker drag) only touch custom properties on the host.
  // CSS added by hand (#998, choices.css) is not drawn into the thumbnails.
  const html = useMemo(() => {
    const template = compileBuilderTemplate({ layout, banner, font, features, options }, { id: `thumb-${layout}` })
    const variant = template.variants[mode]
    const frame = `border:${variant.branding.layout.card.borderWidth} solid ${variant.branding.colors.borderColor};border-radius:14px`
    return `<style>:host{display:block;width:380px;zoom:.45}.doc{overflow:hidden;background:var(--bg);color:var(--text);font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;${frame}}.button{height:40px;margin:10px 14px 12px;border-radius:10px;background:var(--accent)}${LEG_LAYOUT_CSS}${template.cssCode.replace(FONT_FACE_RULE_PATTERN, '')}</style><div class="doc">${injectPackTokens(template.htmlCode, THUMBNAIL_PACK)}<div class="button"></div></div>`
  }, [layout, banner, font, features, options, mode])
  const variables = useMemo(() => builderVariables({ palette, overrides }, mode), [palette, overrides, mode])
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    const root = host.shadowRoot || host.attachShadow({ mode: 'open' })
    // The markup is the builder's own template with every pack value HTML-escaped by injectPackTokens.
    root.innerHTML = html
  }, [html])
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    for (const variable of variables) host.style.setProperty(variable.key, variable.value)
  }, [variables])
  return <span ref={hostRef} />
}

// Memoized with its thumbnails: typing the template name changes none of its props.
const LayoutStep = memo(function LayoutStep({ choices, thumbnailChoices, mode, disabled, onChange }) {
  const groupRef = useRef(null)
  useEffect(loadThumbnailFont, [])
  // Radio group keys: arrows, Home and End move the selection, and only the selected tile is a tab stop.
  const onKeyDown = (event) => {
    const index = BUILDER_LAYOUTS.findIndex((layout) => layout.id === choices.layout)
    const last = BUILDER_LAYOUTS.length - 1
    const next = {
      ArrowRight: index === last ? 0 : index + 1, ArrowDown: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1, ArrowUp: index === 0 ? last : index - 1,
      Home: 0, End: last,
    }[event.key]
    if (next === undefined || disabled) return
    event.preventDefault()
    onChange({ ...choices, layout: BUILDER_LAYOUTS[next].id })
    const tile = groupRef.current?.querySelectorAll('[role="radio"]')[next]
    tile?.focus()
    tile?.scrollIntoView?.({ block: 'nearest' })
  }
  return (
    <div ref={groupRef} role="radiogroup" aria-label="Layout" onKeyDown={onKeyDown} className="grid grid-cols-2 gap-3 p-0.5">
      {BUILDER_LAYOUTS.map((layout) => {
        const selected = choices.layout === layout.id
        return (
          <button key={layout.id} type="button" role="radio" aria-checked={selected} tabIndex={selected ? 0 : -1} disabled={disabled} title={layout.description} onClick={() => onChange({ ...choices, layout: layout.id })}
            className={`group relative overflow-hidden rounded-xl border bg-[var(--prepacks-bg)] text-left transition-[border-color,box-shadow] duration-150 disabled:opacity-60 ${selected ? SELECTED_TILE : UNSELECTED_TILE}`}>
            <span className="pointer-events-none block h-[128px] overflow-hidden px-2 pt-2" aria-hidden="true" inert>
              <LayoutThumbnail layout={layout.id} banner={thumbnailChoices.banner} font={thumbnailChoices.font} features={thumbnailChoices.features} options={thumbnailChoices.options} palette={thumbnailChoices.palette} overrides={thumbnailChoices.overrides} mode={mode} />
            </span>
            <span className="flex items-center justify-between gap-2 border-t border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-3 py-2 text-xs font-medium text-[var(--prepacks-text)]">
              {layout.name}
              {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-[var(--prepacks-accent-text)]" aria-hidden="true" /> : null}
            </span>
          </button>
        )
      })}
    </div>
  )
})

/** Shown on the choice tabs once the code was edited by hand: those choices no longer generate anything. */
function CodeLockNote({ onDiscard, disabled }) {
  return (
    <p className="mb-4 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-2.5 text-xs text-[var(--prepacks-muted)]">
      The HTML and CSS were edited by hand, so these choices no longer change the card.{' '}
      <button type="button" disabled={disabled} onClick={onDiscard} className="text-[var(--prepacks-accent-text)] underline-offset-2 hover:underline disabled:opacity-50">Discard the code edits</button> to use them again.
    </p>
  )
}

const SHELL_GROUPS = [
  ['Button', SHELL_FIELDS.filter(([path]) => path.includes('cta'))],
  ['Odds badge', SHELL_FIELDS.filter(([path]) => path.includes('Odds') || path.includes('odds'))],
  ['Footer and frame', SHELL_FIELDS.filter(([path]) => path.includes('prepackActions') || path.includes('card') || path === 'colors.borderColor')],
]
const CODE_FIELDS = [['html', 'Shared HTML'], ['css', 'Shared CSS'], ['lightCss', 'Light card CSS'], ['darkCss', 'Dark card CSS']]
// CSS added after the generated CSS (#998, choices.css): it locks none of the choices.
const ADDED_CSS_LABELS = { shared: 'Shared CSS', light: 'Light card CSS', dark: 'Dark card CSS' }
const ADDED_CSS_FIELDS = ADDED_CSS_KEYS.map((key) => [key, ADDED_CSS_LABELS[key]])

/** One native-shell setting: a color picker when the value is a hex, a text field otherwise (sizes, var() references). */
function ShellField({ label, type, value, placeholder, disabled, onChange }) {
  const id = useId()
  const pickerValue = HEX_COLOR_PATTERN.test(value || placeholder || '') ? (value || placeholder) : null
  return (
    <div className="text-xs">
      <label htmlFor={id} className="mb-1.5 block text-[var(--prepacks-text)]">{label}</label>
      <span className="flex items-center gap-2">
        {type === 'color' && pickerValue ? <input type="color" aria-label={`${label} picker`} value={pickerValue} disabled={disabled} onChange={(event) => onChange(event.target.value)} className="h-8 w-8 shrink-0 cursor-pointer rounded border border-[var(--prepacks-border)] bg-transparent disabled:opacity-60" /> : null}
        <input id={id} value={value} placeholder={placeholder} disabled={disabled} spellCheck={false} autoComplete="off" maxLength={64} onChange={(event) => onChange(event.target.value)} className={`${FIELD} h-8 text-xs`} />
      </span>
    </div>
  )
}

/** CSS added after the generated CSS (#998): it locks none of the choices; the generated CSS is shown to read. */
function AddedCssSection({ choices, disabled, onChange }) {
  const idPrefix = useId()
  // Kept as typed while editing; compiling and saving (normalizeBuilderChoices) drop the key once all three are blank.
  const setAddedCss = (key, value) => onChange({ ...choices, css: { ...EMPTY_ADDED_CSS, ...choices.css, [key]: value } })
  const generatedCss = useMemo(() => compileBuilderTemplate({ ...choices, css: undefined }).cssCode, [choices])
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">Added CSS</h3>
      <p className="text-xs text-[var(--prepacks-muted)]">Added after the CSS your choices generate, so it can change any of it without locking the Layout, Features and Colors tabs. The Colors tab's names are CSS variables here, like var(--accent).</p>
      {ADDED_CSS_FIELDS.map(([key, label]) => (
        <div key={key}>
          <label htmlFor={`${idPrefix}-${key}`} className="mb-1.5 block text-xs font-medium text-[var(--prepacks-text)]">{label}</label>
          <textarea id={`${idPrefix}-${key}`} value={choices.css?.[key] ?? ''} disabled={disabled} spellCheck={false} onChange={(event) => setAddedCss(key, event.target.value)} className={`${FIELD} min-h-[120px] py-2 font-mono text-xs leading-relaxed`} />
        </div>
      ))}
      <details className="text-xs">
        <summary className="cursor-pointer text-[var(--prepacks-muted)] hover:text-[var(--prepacks-text)]">Show the generated CSS</summary>
        <label htmlFor={`${idPrefix}-generated`} className="mb-1.5 mt-3 block text-xs font-medium text-[var(--prepacks-text)]">Generated CSS</label>
        <textarea id={`${idPrefix}-generated`} value={generatedCss} readOnly spellCheck={false} className={`${FIELD} min-h-[160px] py-2 font-mono text-xs leading-relaxed`} />
      </details>
    </section>
  )
}

/**
 * Everything the generated card does not ask about: the template's
 * description, the native button, odds badge and footer, CSS added after
 * the generated CSS (#998), and the HTML and CSS themselves.
 */
function AdvancedStep({ choices, description, previewTemplate, disabled, onChange, onDescriptionChange, onEditCode, onDiscardCode }) {
  const idPrefix = useId()
  const [mode, setMode] = useState('light')
  const generated = previewTemplate.variants[mode].branding
  const read = (object, path) => path.split('.').reduce((value, key) => value?.[key], object)
  const setShell = (path, value) => {
    const fields = { ...choices.shell[mode] }
    if (value.trim()) fields[path] = value
    else delete fields[path]
    onChange({ ...choices, shell: { ...choices.shell, [mode]: fields } })
  }
  const code = choices.code
  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-2 text-sm font-semibold">Details</h3>
        <label htmlFor={`${idPrefix}-description`} className="mb-1.5 block text-xs text-[var(--prepacks-text)]">Description</label>
        <input id={`${idPrefix}-description`} value={description} disabled={disabled} maxLength={140} onChange={(event) => onDescriptionChange(event.target.value)} placeholder="When to use this template; shown under its name in the list" className={`${FIELD} h-8 text-xs`} />
      </section>
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Button, odds and footer</h3>
          <div className={SEGMENT} role="group" aria-label="Card to style">
            {BUILDER_MODES.map((item) => (
              <button key={item} type="button" aria-pressed={item === mode} onClick={() => setMode(item)} className={segmentButton(item === mode)}>{item === 'light' ? 'Light card' : 'Dark card'}</button>
            ))}
          </div>
        </div>
        <p className="text-xs text-[var(--prepacks-muted)]">Drawn by the widget around the artwork. Empty fields keep what the layout sets; var(--accent) and the other color names from the Colors tab work here.</p>
        {SHELL_GROUPS.map(([title, fields]) => (
          <fieldset key={title} className="grid min-w-0 grid-cols-2 gap-3">
            <legend className="mb-2 text-xs font-semibold text-[var(--prepacks-muted)]">{title}</legend>
            {fields.map(([path, label, type]) => (
              <ShellField key={path} label={label} type={type} value={choices.shell[mode][path] || ''} placeholder={String(read(generated, path) ?? '')} disabled={disabled} onChange={(value) => setShell(path, value)} />
            ))}
          </fieldset>
        ))}
      </section>
      {code ? null : <AddedCssSection choices={choices} disabled={disabled} onChange={onChange} />}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">HTML and CSS</h3>
        {code ? (
          <>
            <p className="text-xs text-[var(--prepacks-muted)]">Edited by hand: the Layout, Features and Banner choices no longer change the card. Placeholders like {'{{TITLE}}'} are filled with the pack, and the Colors tab's names are CSS variables here.{choices.css ? ' The added CSS is part of the CSS below and applies again on its own if you discard the code edits.' : ''}</p>
            {CODE_FIELDS.map(([key, label]) => (
              <div key={key}>
                <label htmlFor={`${idPrefix}-${key}`} className="mb-1.5 block text-xs font-medium text-[var(--prepacks-text)]">{label}</label>
                <textarea id={`${idPrefix}-${key}`} value={code[key]} disabled={disabled} spellCheck={false} onChange={(event) => onChange({ ...choices, code: { ...code, [key]: event.target.value } })} className={`${FIELD} min-h-[160px] py-2 font-mono text-xs leading-relaxed`} />
              </div>
            ))}
            <button type="button" disabled={disabled} onClick={onDiscardCode} className={QUIET_BUTTON}>Discard code edits</button>
          </>
        ) : (
          <>
            <p className="text-xs text-[var(--prepacks-muted)]">The HTML is generated from your choices. If you edit the HTML and CSS by hand, the Layout, Features and Banner choices stop applying, and the added CSS is copied into the code.</p>
            <button type="button" disabled={disabled} onClick={onEditCode} className={QUIET_BUTTON}>Edit the HTML and CSS by hand</button>
          </>
        )}
      </section>
    </div>
  )
}

/** The header banner: none, a gradient or two-zone split drawn from the two colors, or a picture (behind the header or the whole card). */
function BannerPicker({ choices, orgSlug, disabled, problem, sceneRef, scheduleChanges, cardAspect, headerAspect, paint, onChange, onUploadChange }) {
  const [main, accent] = choices.palette
  const swatch = {
    none: 'var(--prepacks-bg)',
    gradient: `linear-gradient(135deg, ${main}, color-mix(in srgb, ${main} 55%, black))`,
    split: `linear-gradient(115deg, ${main} 0 66%, ${accent} 66% 100%)`,
    image: `linear-gradient(90deg, ${main} 0%, color-mix(in srgb, ${main} 30%, transparent) 100%), ${SAMPLE_BANNER_VALUE} center/cover`,
  }
  return (
    <fieldset className="min-w-0">
      <legend className="sr-only">Banner</legend>
      <div className="grid grid-cols-2 gap-2 p-0.5 sm:grid-cols-4">
        {BUILDER_BANNERS.map((banner) => {
          const selected = choices.banner === banner.id
          // Picking the image banner keeps whatever picture settings were set before; a first pick allows AI pictures
          // and asks for the default scene.
          const pick = () => onChange({ ...choices, banner: banner.id, ...(banner.id === BANNER_IMAGE && !choices.image ? { image: { ai_allowed: true, prompt: '' } } : {}) })
          return (
            <button key={banner.id} type="button" aria-pressed={selected} disabled={disabled} title={banner.description} onClick={pick}
              className={`overflow-hidden rounded-xl border text-left transition-[border-color,box-shadow] duration-150 disabled:opacity-60 ${selected ? SELECTED_TILE : UNSELECTED_TILE}`}>
              <span className="block h-7" style={{ background: swatch[banner.id] }} aria-hidden="true" />
              <span className="flex items-center justify-between gap-1 border-t border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-2.5 py-1.5 text-xs font-medium text-[var(--prepacks-text)]">
                {banner.name}
                {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-[var(--prepacks-accent-text)]" aria-hidden="true" /> : null}
              </span>
            </button>
          )
        })}
      </div>
      {choices.banner === BANNER_IMAGE ? <BannerImageControls choices={choices} orgSlug={orgSlug} disabled={disabled} problem={problem} sceneRef={sceneRef} cardAspect={cardAspect} headerAspect={headerAspect} paint={paint} onChange={onChange} onUploadChange={onUploadChange} /> : null}
      {/* Here, not with the picture settings: leaving the image banner changes schedules' pictures too. */}
      <ScheduleBannerNotice changes={scheduleChanges} />
    </fieldset>
  )
}

const BANNER_FILLS = [[BANNER_FILL_HEADER, 'Header'], [BANNER_FILL_CARD, 'Whole card']]
// The native footer under a whole-card picture (#1038): the solid banner fade, or nothing, so the picture runs on under the odds button.
const BANNER_FOOTERS = [[BANNER_FOOTER_TINTED, 'Tinted'], [BANNER_FOOTER_TRANSPARENT, 'Transparent']]
const TITLE_PLACEMENT_OPTIONS = [[TITLE_PLACEMENT_OVER, 'Over image'], [TITLE_PLACEMENT_BELOW, 'Below image'], [TITLE_PLACEMENT_NONE, 'No title']]
const TITLE_ALIGNMENT_OPTIONS = [[TITLE_ALIGNMENT_LEFT, 'Left'], [TITLE_ALIGNMENT_CENTER, 'Center']]
// The Banner card's summary names a title that is not over the picture, so a folded card still tells (#946).
const TITLE_SUMMARY = { [TITLE_PLACEMENT_BELOW]: 'Title below', [TITLE_PLACEMENT_NONE]: 'No title' }

/** The name of the catalog entry `id`, for a choice card's summary. */
const pickName = (list, id) => list.find((item) => item.id === id)?.name

/** The Banner card's summary: the banner's name, and for a picture a title that is not over it. */
function bannerSummary(choices) {
  const name = pickName(BUILDER_BANNERS, choices.banner) || ''
  if (choices.banner !== BANNER_IMAGE) return name
  const placement = TITLE_SUMMARY[bannerTitleOf(choices.title, bannerFillOf(choices.image)).placement]
  return placement ? `${name} · ${placement}` : name
}

/**
 * Where the pack title sits on the picture and how it is aligned (#946). Alignment fades out under "No title"
 * and keeps its value, so the title comes back as it was. Nothing sits below a picture behind the whole card.
 */
function TitleControls({ title, fill, disabled, onChange }) {
  const id = useId()
  const noTitle = title.placement === TITLE_PLACEMENT_NONE
  const card = fill === BANNER_FILL_CARD
  return (
    <div className="space-y-2 text-xs text-[var(--prepacks-muted)]">
      <div className="flex flex-wrap items-center gap-2">
        <span id={`${id}-placement`} className="w-24 shrink-0">Title placement</span>
        <div className={SEGMENT} role="group" aria-labelledby={`${id}-placement`}>
          {TITLE_PLACEMENT_OPTIONS.map(([value, label]) => {
            const unavailable = card && value === TITLE_PLACEMENT_BELOW
            return (
              <button key={value} type="button" aria-pressed={title.placement === value} disabled={disabled || unavailable}
                title={unavailable ? 'A picture behind the whole card has nothing below it' : undefined}
                onClick={() => onChange({ ...title, placement: value })} className={`${segmentButton(title.placement === value)}${unavailable ? ' cursor-not-allowed opacity-50' : ''}`}>{label}</button>
            )
          })}
        </div>
      </div>
      <div className={`flex flex-wrap items-center gap-2 ${noTitle ? 'opacity-60' : ''}`}>
        <span id={`${id}-alignment`} className="w-24 shrink-0">Title alignment</span>
        <div className={SEGMENT} role="group" aria-labelledby={`${id}-alignment`} aria-describedby={noTitle ? `${id}-alignment-note` : undefined}>
          {TITLE_ALIGNMENT_OPTIONS.map(([value, label]) => (
            <button key={value} type="button" aria-pressed={title.alignment === value} disabled={disabled || noTitle}
              onClick={() => onChange({ ...title, alignment: value })} className={segmentButton(title.alignment === value)}>{label}</button>
          ))}
        </div>
        {noTitle ? <p id={`${id}-alignment-note`} className="basis-full pl-[104px]">Choose a title placement to enable alignment.</p> : null}
      </div>
    </div>
  )
}

/**
 * The image banner's picture settings. Where every picture goes (the header, or the whole card under a scrim),
 * whatever its source, and where the pack title sits on it (#946). Behind the whole card the native footer is tinted
 * or transparent, so the picture runs on under the odds button (#1038). The template never paints: it can carry one
 * fixed picture, which every pack shows unless the pack or its schedule has its own, and it can allow AI pictures,
 * which packs and schedules paint from the default scene. Without a fixed picture the default scene is required.
 * The scene is kept while AI is off, so turning it back on loses nothing typed (it is not saved). `cardAspect` is
 * the preview card's shape and `headerAspect` its header picture's, which the framing box takes for the whole-card
 * and the header fill.
 */
function BannerImageControls({ choices, orgSlug, disabled, problem, sceneRef, cardAspect, headerAspect, paint, onChange, onUploadChange }) {
  const id = useId()
  const mediaAccess = usePictureMediaAccess()
  const fill = bannerFillOf(choices.image)
  const footer = bannerFooterOf(choices.image)
  const image = { ai_allowed: true, prompt: '', url: '', position: defaultBannerPosition(fill), scale: MIN_BANNER_SCALE, ...choices.image, fill }
  // Uploads may finish after another setting changed. Only replace the
  // picture fields; keep the current AI choice, scene, fill and other card edits.
  const latest = useRef(choices)
  latest.current = choices
  const setImage = (patch) => {
    paint?.onChange?.()
    const current = latest.current
    onChange({ ...current, image: { ...current.image, ...patch } })
  }
  const setTitle = (next) => { paint?.onChange?.(); onChange({ ...latest.current, title: next }) }
  const title = bannerTitleOf(choices.title, fill)
  // Each fill frames the picture its own way (banner widths or card heights), so a switch starts from that fill's
  // framing, and nothing sits below a picture behind the whole card: the title goes over it, its alignment kept. The
  // whole card starts with the tinted footer, and a header picture has none.
  const setFill = (next) => {
    if (next === fill) return
    paint?.onChange?.()
    const current = latest.current
    const { footer: _footer, ...rest } = current.image || {}
    onChange({ ...current, image: { ...rest, fill: next, ...(next === BANNER_FILL_CARD ? { footer: BANNER_FOOTER_TINTED } : {}), position: defaultBannerPosition(next), scale: MIN_BANNER_SCALE }, title: bannerTitleOf(current.title, next) })
  }
  // The framing box wears what the card puts over the picture (bannerFramingWash). The banner fade depends on the
  // colors alone, so it is placed again only when they change.
  const { palette, overrides } = choices
  const headerEnd = useMemo(() => builderVariables({ palette, overrides }, 'light').find((variable) => variable.key === '--header-end').value, [palette, overrides])
  const shade = bannerFramingWash({ fill, title, main: palette[0], headerEnd })
  const fixed = isBannerImageUrl(image.url) ? image.url : null
  const urlInvalid = Boolean(image.url) && !fixed
  const framingPicture = fixed ?? paint?.painted
  const prompt = image.prompt.trim()
  const shuffle = () => {
    const rest = BANNER_SCENE_SUGGESTIONS.filter((scene) => scene !== prompt)
    setImage({ prompt: rest[Math.floor(Math.random() * rest.length)] })
  }
  const switchId = `${id}-ai`
  const problemId = problem ? `${id}-problem` : undefined
  return (
    <div className="mt-3 space-y-3">
      <div className={`${SEGMENT} w-fit`} role="group" aria-label="Picture fill">
        {BANNER_FILLS.map(([value, label]) => (
          <button key={value} type="button" aria-pressed={fill === value} disabled={disabled} onClick={() => setFill(value)} className={segmentButton(fill === value)}>{label}</button>
        ))}
      </div>
      {fill === BANNER_FILL_CARD ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--prepacks-muted)]">
          <span id={`${id}-footer`} className="w-24 shrink-0">Footer</span>
          <div className={SEGMENT} role="group" aria-labelledby={`${id}-footer`} aria-describedby={`${id}-footer-note`}>
            {BANNER_FOOTERS.map(([value, label]) => (
              <button key={value} type="button" aria-pressed={footer === value} disabled={disabled} onClick={() => setImage({ footer: value })} className={segmentButton(footer === value)}>{label}</button>
            ))}
          </div>
          <p id={`${id}-footer-note`} className="basis-full pl-[104px]">{footer === BANNER_FOOTER_TRANSPARENT ? 'The picture runs on under the odds button.' : 'The odds button sits on a solid band in the banner fade.'}</p>
        </div>
      ) : null}
      <TitleControls title={title} fill={fill} disabled={disabled} onChange={setTitle} />
      <div>
        {/* A new picture starts from the default framing: the old one's crop rarely fits it. */}
        <NativeAssetControl slug={orgSlug} field={{ path: 'url', label: 'Fixed picture' }} value={image.url} disabled={disabled} {...mediaAccess} onBusyChange={onUploadChange} onChange={(_, url) => setImage(url === latest.current.image?.url ? { url } : { url, position: defaultBannerPosition(bannerFillOf(latest.current.image)), scale: MIN_BANNER_SCALE })} />
        <p className="mt-1.5 text-xs text-[var(--prepacks-muted)]">Optional. Every pack shows it unless the pack or its schedule has its own.</p>
        {urlInvalid ? <p role="alert" className="mt-1 text-xs text-[var(--wf-color-danger)]">Pictures load from https URLs without spaces or quotes.</p> : null}
      </div>
      {framingPicture ? <BannerFraming picture={framingPicture} main={shade.main} wash={shade.wash} fill={fill} aspect={cardAspect} headerAspect={headerAspect} position={image.position} scale={image.scale} disabled={disabled} onChange={(framing) => setImage(framing)} /> : null}
      <div className="rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)]">
        {/* The whole row is the switch, like the feature switches, so the label and its hint are part of the target. */}
        <button ref={image.ai_allowed ? undefined : sceneRef} type="button" role="switch" aria-checked={image.ai_allowed} aria-labelledby={switchId} aria-describedby={[`${switchId}-hint`, image.ai_allowed ? null : problemId].filter(Boolean).join(' ')} disabled={disabled}
          onClick={() => setImage({ ai_allowed: !image.ai_allowed })}
          className="flex w-full items-center justify-between gap-4 rounded-lg px-3 py-2.5 text-left hover:bg-[var(--prepacks-raised)]/60 disabled:opacity-60">
          <span className="min-w-0 text-xs">
            <span id={switchId} className="block font-medium text-[var(--prepacks-text)]">Allow AI pictures</span>
            <span id={`${switchId}-hint`} className="block text-[var(--prepacks-muted)]">Packs and schedules that use this template can generate their own picture.</span>
          </span>
          <span aria-hidden="true" className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${image.ai_allowed ? 'bg-[var(--prepacks-accent)]' : 'bg-[var(--prepacks-border)]'}`}>
            <span className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-[var(--wf-color-on-action)] transition-transform ${image.ai_allowed ? 'translate-x-4' : ''}`} />
          </span>
        </button>
        {image.ai_allowed ? (
          <div className="space-y-1.5 border-t border-[var(--prepacks-border)] px-3 pb-3 pt-2.5 text-xs">
            <label htmlFor={`${id}-scene`} className="block font-medium text-[var(--prepacks-text)]">Scene{fixed ? null : <span className="text-[var(--wf-color-danger)]" aria-hidden="true"> *</span>}</label>
            <div className="flex items-center gap-2">
              <input ref={sceneRef} id={`${id}-scene`} name="banner-scene" autoComplete="off" aria-required={!fixed || undefined} aria-invalid={problem ? true : undefined} aria-describedby={[problemId, `${id}-scene-help`].filter(Boolean).join(' ')}
                value={image.prompt} disabled={disabled} placeholder="Stadium at night under floodlights…" title={image.prompt || undefined}
                onChange={(event) => setImage({ prompt: clampRunes(event.target.value) })} className={`${FIELD} h-9 text-sm ${problem ? '!border-[var(--wf-color-danger)]' : ''}`} />
              <button type="button" onClick={shuffle} disabled={disabled} title="Try an example scene" aria-label="Try an example scene" className={`${QUIET_BUTTON} inline-flex h-9 items-center justify-center !px-2.5 !py-0`}><Dices className="h-4 w-4" aria-hidden="true" /></button>
              {!fixed ? <button type="button" onClick={paint?.onGenerate} disabled={disabled || (!paint?.painting && !prompt)} aria-disabled={paint?.painting || undefined}
                className={`${QUIET_BUTTON} inline-flex h-9 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap !py-0`}>
                {paint?.painting ? <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />}
                {paint?.painting ? 'Generating preview…' : 'Generate preview'}
              </button> : null}
            </div>
            <p id={`${id}-scene-help`} className="text-[var(--prepacks-muted)]">Where a new picture starts; each pack or schedule can change it. The pack’s game is added. No logos or players.</p>
          </div>
        ) : null}
      </div>
      {problem ? <p id={problemId} role="alert" className="text-xs text-[var(--wf-color-danger)]">{problem}</p> : null}
      {image.ai_allowed && !fixed ? <p role="status" className="text-xs text-[var(--prepacks-muted)]">{paint?.painting ? 'Generating a preview of the scene…' : (paint?.previewPainted || paint?.painted) ? 'The preview shows your scene.' : 'The preview shows a sample picture.'}</p> : null}
      {paint?.error ? <p aria-live="polite" className="text-xs text-[var(--wf-color-danger)]">{paint.error}</p> : null}
    </div>
  )
}

// One line per kind of change, naming the schedules it reaches: the words for one schedule, then for several.
// Saving reaches the packs the schedules already made (#1089), so the lines say what their packs do once saved.
const SCHEDULE_CHANGE_LINES = [
  ['toStatic', 'uses “New picture per pack”. Its packs switch to the same picture.', 'use “New picture per pack”. Their packs switch to the same picture.'],
  ['toColors', 'uses this template’s picture. Its packs switch to the card’s colors.', 'use this template’s picture. Their packs switch to the card’s colors.'],
  ['toDynamic', 'uses this template’s picture. Its packs switch to a new picture each.', 'use this template’s picture. Their packs switch to a new picture each.'],
  ['toNone', 'shows pictures. Its packs switch to the card’s colors.', 'show pictures. Their packs switch to the card’s colors.'],
]
const SHOWN_SCHEDULE_NAMES = 3

function scheduleNames(names) {
  const shown = names.slice(0, SHOWN_SCHEDULE_NAMES).join(', ')
  return names.length > SHOWN_SCHEDULE_NAMES ? `${shown} and ${names.length - SHOWN_SCHEDULE_NAMES} more` : shown
}

/**
 * Schedules whose packs get another picture once this template is saved. A note, not a block: saving goes ahead.
 * The live region stays mounted, so a line that appears as the operator changes the picture is announced.
 */
function ScheduleBannerNotice({ changes }) {
  const lines = SCHEDULE_CHANGE_LINES.filter(([key]) => changes?.[key]?.length)
  return (
    <div role="status" className={lines.length ? 'space-y-1 rounded-lg border border-[var(--wf-color-warning-border)] bg-[var(--wf-color-warning-soft)] px-3 py-2 text-xs text-[var(--wf-color-warning)]' : 'sr-only'}>
      {lines.map(([key, one, several]) => <p key={key}><strong>{scheduleNames(changes[key])}</strong> {changes[key].length === 1 ? one : several}</p>)}
    </div>
  )
}

// How many icons show before the "+": the common ones, the rest a tap away.
const ICONS_SHOWN = 3

/** One setting of a feature: a row of choices; icon choices draw the icon itself. `note` explains the setting under it. */
function FeatureOption({ feature, group, value, disabled, note, onChange }) {
  const noteId = useId()
  const [expanded, setExpanded] = useState(false)
  const isIcons = group.key === 'icon'
  const chosenIndex = group.values.findIndex((option) => option.id === value)
  const visible = isIcons && !expanded && chosenIndex < ICONS_SHOWN ? group.values.slice(0, ICONS_SHOWN) : group.values
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--prepacks-muted)]">
      <span className="w-14 shrink-0">{group.label}</span>
      <div className={SEGMENT} role="group" aria-label={`${feature.label} ${group.label.toLowerCase()}`} aria-describedby={note ? noteId : undefined}>
        {visible.map((option) => (
          <button key={option.id} type="button" aria-pressed={option.id === value} aria-label={isIcons ? option.name : undefined} title={isIcons ? option.name : undefined} disabled={disabled} onClick={() => onChange(option.id)} className={segmentButton(option.id === value)}>
            {isIcons && option.id !== 'none'
              ? <span className="block h-4 w-4 [&>svg]:h-4 [&>svg]:w-4" dangerouslySetInnerHTML={{ __html: counterIcon(option.id) }} />
              : option.name}
          </button>
        ))}
        {isIcons && visible.length < group.values.length ? (
          <button type="button" aria-label="More icons" title="More icons" disabled={disabled} onClick={() => setExpanded(true)} className={segmentButton(false)}><Plus className="h-4 w-4" aria-hidden="true" /></button>
        ) : null}
      </div>
      {/* Lined up under the choices: the label column (w-14) plus the gap. */}
      {note ? <p id={noteId} className="basis-full pl-16">{note}</p> : null}
    </div>
  )
}

const PREVIEW_FAMILIES = { system: 'inherit', ...BUILDER_FONT_FAMILIES }

/** The title font: the widget's own, or one of the two bundled display fonts, each shown in itself. */
function FontPicker({ choices, disabled, onChange }) {
  useEffect(loadThumbnailFont, [])
  const fixed = layoutHasOwnFont(choices.layout)
  return (
    <fieldset className="min-w-0">
      <legend className="sr-only">Title font</legend>
      {fixed ? <p className="mb-2 text-xs text-[var(--prepacks-muted)]">The Editorial layout has its own font (Oswald); pick another layout to change it.</p> : null}
      <div className="grid grid-cols-3 gap-3 p-0.5">
        {BUILDER_FONTS.map((font) => {
          const selected = choices.font === font.id
          return (
            <button key={font.id} type="button" aria-pressed={selected} disabled={disabled || fixed} title={font.description} onClick={() => onChange({ ...choices, font: font.id })}
              className={`overflow-hidden rounded-xl border bg-[var(--prepacks-bg)] text-left transition-[border-color,box-shadow] duration-150 disabled:opacity-60 ${selected ? SELECTED_TILE : UNSELECTED_TILE}`}>
              <span className={`block truncate px-3 pb-1 pt-2 text-lg leading-tight text-[var(--prepacks-text)] ${font.id === 'oswald' ? 'uppercase' : ''}`} style={{ fontFamily: PREVIEW_FAMILIES[font.id], fontWeight: font.id === 'lilita' ? 400 : 700 }} aria-hidden="true">Night SGP</span>
              <span className="flex items-center justify-between gap-2 border-t border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-3 py-2 text-xs font-medium text-[var(--prepacks-text)]">
                {font.name}
                {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-[var(--prepacks-accent-text)]" aria-hidden="true" /> : null}
              </span>
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}

// A second line under a feature's own description.
const FEATURE_NOTES = {
  logos: 'Real packs show the team’s logo where the organization may show it.',
}
// A line under one setting of a feature, by feature and setting key.
const OPTION_NOTES = {}

function FeaturesStep({ choices, disabled, onChange }) {
  const idPrefix = useId()
  return (
    <ul className="space-y-2">
      {BUILDER_FEATURES.map((feature) => {
        const on = choices.features[feature.key]
        const labelId = `${idPrefix}-${feature.key}`
        return (
          <li key={feature.key} className="rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)]">
            {/* The whole row is the switch, so the label and its explanation are part of the target. */}
            <button type="button" role="switch" aria-checked={on} aria-labelledby={labelId} aria-describedby={`${labelId}-hint`} disabled={disabled} onClick={() => onChange({ ...choices, features: { ...choices.features, [feature.key]: !on } })}
              className="flex w-full items-center justify-between gap-4 rounded-lg px-3 py-2.5 text-left hover:bg-[var(--prepacks-raised)]/60 disabled:opacity-60">
              <span className="min-w-0 text-xs">
                <span id={labelId} className="block font-medium text-[var(--prepacks-text)]">{feature.label}</span>
                <span id={`${labelId}-hint`} className="block text-[var(--prepacks-muted)]">
                  {feature.description}
                  {FEATURE_NOTES[feature.key] ? <span className="mt-1 block">{FEATURE_NOTES[feature.key]}</span> : null}
                </span>
              </span>
              <span aria-hidden="true" className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${on ? 'bg-[var(--prepacks-accent)]' : 'bg-[var(--prepacks-border)]'}`}>
                <span className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-[var(--wf-color-on-action)] transition-transform ${on ? 'translate-x-4' : ''}`} />
              </span>
            </button>
            {feature.options?.some((group) => !group.retired) && on ? (
              // The feature's settings live inside its card, folded behind "Options"; retired settings are
              // omitted while any stored values continue to round-trip.
              <details className="group border-t border-[var(--prepacks-border)]" open>
                <summary className="flex cursor-pointer list-none items-center gap-1.5 px-3 py-2 text-xs font-medium text-[var(--prepacks-muted)] hover:text-[var(--prepacks-text)] [&::-webkit-details-marker]:hidden">
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />Options
                </summary>
                <div className="space-y-2 px-3 pb-3">
                  {feature.options.filter((group) => !group.retired).map((group) => (
                    <FeatureOption key={group.key} feature={feature} group={group} value={choices.options[feature.key][group.key]} disabled={disabled} note={OPTION_NOTES[feature.key]?.[group.key]}
                      onChange={(value) => onChange({ ...choices, options: { ...choices.options, [feature.key]: { ...choices.options[feature.key], [group.key]: value } } })} />
                  ))}
                </div>
              </details>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}

function ColorsStep({ choices, previewTemplate, teamsQuery, previewTeam, disabled, onChange }) {
  // Which card the individual adjustments below apply to; the preview always shows both.
  const [mode, setMode] = useState('light')
  // Already computed for the preview; the same function would produce the same array.
  const variables = previewTemplate.variants[mode].cssVars
  const adjusted = Object.keys(choices.overrides[mode]).length
  const setPalette = (index, value) => onChange({ ...choices, palette: choices.palette.map((color, colorIndex) => (colorIndex === index ? value : color)) })
  const setOverride = (key, value) => onChange({ ...choices, overrides: { ...choices.overrides, [mode]: { ...choices.overrides[mode], [key]: value } } })
  return (
    <div className="space-y-5">
      <TeamPicker choices={choices} teamsQuery={teamsQuery} previewTeam={previewTeam} disabled={disabled} onChange={onChange} />
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        <HexField label="Main color" value={choices.palette[0]} disabled={disabled} onChange={(value) => setPalette(0, value)} />
        <button type="button" disabled={disabled} onClick={() => onChange({ ...choices, palette: [choices.palette[1], choices.palette[0]] })} aria-label="Swap main and accent colors" title="Swap colors" className="grid h-8 w-8 place-items-center rounded border border-[var(--prepacks-border)] text-[var(--prepacks-muted)] hover:border-[var(--prepacks-accent-text)] hover:text-[var(--prepacks-accent-text)] disabled:opacity-50">
          <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <HexField label="Accent color" value={choices.palette[1]} disabled={disabled} onChange={(value) => setPalette(1, value)} />
      </div>
      <details className="group rounded-lg border border-[var(--prepacks-border)]">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-xs font-medium text-[var(--prepacks-text)] [&::-webkit-details-marker]:hidden">
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--prepacks-muted)] transition-transform group-open:rotate-90" aria-hidden="true" />
          Adjust individual colors
        </summary>
        <div className="space-y-3 border-t border-[var(--prepacks-border)] p-3">
          <div className="flex items-center justify-between gap-3">
            <div className={SEGMENT} role="group" aria-label="Card to adjust">
              {BUILDER_MODES.map((item) => (
                <button key={item} type="button" aria-pressed={item === mode} onClick={() => setMode(item)} className={segmentButton(item === mode)}>{item === 'light' ? 'Light card' : 'Dark card'}</button>
              ))}
            </div>
            {adjusted ? (
              <button type="button" disabled={disabled} onClick={() => onChange({ ...choices, overrides: { ...choices.overrides, [mode]: {} } })} className="min-h-6 text-xs text-[var(--prepacks-accent-text)] underline-offset-2 hover:underline disabled:opacity-50">Reset {adjusted} to automatic</button>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {variables.map((variable) => (
              <HexField key={variable.key} label={variable.label} value={variable.value} disabled={disabled} onChange={(value) => setOverride(variable.key, value)} />
            ))}
          </div>
        </div>
      </details>
    </div>
  )
}

/** Preview buttons respond like the real one: a press shows the "Added!" state for a moment. Nothing is placed. */
function usePreviewAction() {
  const [addedId, setAddedId] = useState(null)
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])
  const onAction = (pack) => {
    clearTimeout(timer.current)
    setAddedId(pack.id)
    timer.current = setTimeout(() => setAddedId(null), 1400)
  }
  return { addedId, onAction }
}

/**
 * Phone: the builder scrolls itself (the workspace around it clips overflow,
 * which would stop the preview sticking) and fills the screen under the app's
 * own bar. Once the choices are scrolled to, the first screen's "Choose by hand" link goes.
 * On a desktop (1024px and up) `phoneHeight` is null and nothing here applies.
 */
function usePhoneLayout({ rootRef, previewRef, editorRef }) {
  const [phoneHeight, setPhoneHeight] = useState(null)
  const [scrolled, setScrolled] = useState(false)
  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return undefined
    const desktop = window.matchMedia('(min-width: 1024px)')
    const fit = () => {
      if (desktop.matches) {
        setPhoneHeight(null)
        return
      }
      // The builder opens from a list the operator may have scrolled far down; measured from
      // there the height would include the scrolled distance and the window, not the builder,
      // would scroll. The builder is a new screen, so it starts at the top.
      if (window.scrollY) window.scrollTo(0, 0)
      setPhoneHeight(Math.max(320, window.innerHeight - root.getBoundingClientRect().top))
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [rootRef])
  // Shrinks after 24px of scrolling and grows back only at the very top, so the preview never flickers around the threshold.
  const onScroll = (event) => {
    const top = event.currentTarget.scrollTop
    setScrolled((current) => (current ? top > 0 : top > 24))
  }
  // Shrink the preview first, then bring the tabs to rest right under the pinned card.
  const chooseByHand = () => {
    setScrolled(true)
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const root = rootRef.current
      const editor = editorRef.current
      const preview = previewRef.current
      if (root && editor && preview) root.scrollTo({ top: editor.offsetTop - preview.offsetHeight - PHONE_HEADER_HEIGHT - 8, behavior: 'smooth' })
    }))
  }
  // Back to the first screen at once. The scroll event would say so a frame later, after a render that still
  // counts the builder as scrolled.
  const toTop = () => {
    if (rootRef.current) rootRef.current.scrollTop = 0
    setScrolled(false)
  }
  return { phone: Boolean(phoneHeight), phoneHeight, scrolled, onScroll, chooseByHand, toTop }
}

/** One card as the widget draws it, artwork and the real button, in one color mode. */
const PreviewCard = memo(function PreviewCard({ template, pack, mode }) {
  const { branding, cssVars, oddsFormat, unavailable } = usePrepackPreview()
  const { addedId, onAction } = usePreviewAction()
  // A new object each render makes React re-check every branding variable.
  const style = useMemo(() => ({ ...cssVars, containerType: 'inline-size' }), [cssVars])
  return (
    <figure className="w-[min(340px,92vw)] shrink-0">
      <figcaption className="mb-2 text-center text-[11px] font-medium text-[var(--prepacks-muted)]">{mode === 'light' ? 'Light' : 'Dark'}</figcaption>
      {/* The widget sizes a card for a feed column; here it fills this box and its artwork scales with it. */}
      <div className="prepacks-surface--density-comfortable [&_.prepack-widget-card]:!w-full" style={style}>
        {unavailable ? <p className="text-sm text-[var(--prepacks-muted)]">Card preview unavailable until branding loads.</p> : (
          <PrepackCardView pack={pack} template={template} branding={branding} colorMode={mode} oddsFormat={oddsFormat} ctaLabel={branding.buttonText || 'Add to Betslip'}
            actionState={addedId === pack.id ? 'success' : 'idle'} onAction={onAction} />
        )}
      </div>
    </figure>
  )
})

const FEED_PREVIEW_SURFACE = { name: 'Prepacks', display_type: 'carousel', layout_config: {} }

/**
 * The template the way bettors meet it: the same 380px frame as Feed →
 * Configuration's mobile preview, in the feed's own theme, with the prepacks
 * row the widget draws holding a few different packs.
 */
const FeedPreview = memo(function FeedPreview({ template, packs, templateVars }) {
  const { branding, oddsFormat } = usePrepackPreview()
  const { addedId, onAction } = usePreviewAction()
  const frameVars = useMemo(() => brandingToCssVars(branding), [branding])
  const items = useMemo(() => packs.map((pack) => toPublicPrepack({ ...pack, template_id: template.id, template_vars: templateVars })), [packs, template.id, templateVars])
  const templates = useMemo(() => [template], [template])
  return (
    <div className="min-h-[420px] w-[380px] max-w-full overflow-hidden rounded-xl p-4 shadow-[0_10px_40px_-16px_rgba(0,0,0,0.5)]" role="group" aria-label="Phone-width feed preview"
      style={{ ...frameVars, background: 'var(--feed-bg, #1a1d2e)', color: 'var(--feed-heading, #ffffff)', fontFamily: 'var(--feed-font-primary, Inter, sans-serif)' }}>
      <PrepacksSurfaceView items={items} templates={templates} surface={FEED_PREVIEW_SURFACE} branding={branding} oddsFormat={oddsFormat} actionSuccessId={addedId} onAction={onAction} />
    </div>
  )
})

const PREVIEW_VIEWS = [['card', 'Card'], ['feed', 'In a feed']]
const TEN_MINUTES = 10 * 60 * 1000
// A stable select keeps react-query's result, and the picker's groups, the same object between renders.
const selectTeams = (data) => (Array.isArray(data?.teams) ? data.teams : [])

/**
 * The template builder: layout, features and colors beside the card they
 * make (the prompt under the card that could fill any of them in is hidden
 * since #996, DESCRIBE_CARD_SHOWN). `template`
 * is a template made here before (it carries `builder`), or null for a new one.
 */
export default function TemplateBuilder({ template, orgSlug, canConfigure, isSaving, onSave, onCancel }) {
  const idPrefix = useId()
  const { branding } = usePrepackPreview()
  const [id] = useState(() => template?.id || newTemplateId())
  const [name, setName] = useState(template?.name || '')
  const [description, setDescription] = useState(template?.description || '')
  // A template whose CSS alone was edited by hand before #998 opens with its choices unlocked (unlockCssOnlyEdits).
  const [choices, setChoices] = useState(() => unlockCssOnlyEdits(template?.builder || BUILDER_DEFAULTS))
  const [uploadingPicture, setUploadingPicture] = useState(false)
  const [tab, setTab] = useState(0)
  const [previewView, setPreviewView] = useState('card')
  // On a phone there is room for one card; the desktop shows both.
  const [phoneMode, setPhoneMode] = useState(() => (branding.darkMode ? 'dark' : 'light'))
  const [nameMissing, setNameMissing] = useState(false)
  const nameRef = useRef(null)
  const editorRef = useRef(null)
  const rootRef = useRef(null)
  const previewRef = useRef(null)
  // The preview's cards (Card or In a feed), whose shape (or their header picture's) the framing box takes.
  const previewCardsRef = useRef(null)
  const cardAspect = useCardAspect(previewCardsRef)
  const headerAspect = useHeaderAspect(previewCardsRef)
  const { phone, phoneHeight, scrolled, onScroll, chooseByHand, toTop } = usePhoneLayout({ rootRef, previewRef, editorRef })
  const disabled = !canConfigure || isSaving
  // The preview and thumbnails follow the choices a beat behind: a color-picker drag keeps the field
  // responsive while stale card renders are dropped. Typing a name re-renders no card at all (the cards are memoized).
  const deferredChoices = useDeferredValue(choices)
  const previewTemplate = useMemo(() => compileBuilderTemplate(deferredChoices, { id }), [deferredChoices, id])
  // The catalog teams for the team picker, and for the nickname the sample copy uses.
  const teamsQuery = useQuery({
    queryKey: ['prepacks', 'template-teams', orgSlug],
    queryFn: ({ signal }) => prepacksApi.fetchTemplateTeams(orgSlug, { signal }),
    enabled: Boolean(orgSlug),
    // The list is built once from the catalog embedded in the server: it never changes while Studio runs.
    staleTime: Infinity,
    retry: 1,
    select: selectTeams,
  })
  // What the preview's sample pack says (Sample content, #602): demo only, never compiled, and saved with the
  // template so it reopens the way it was left (#997). The cards follow it a beat behind, like the choices.
  const [sample, setSample] = useState(() => sampleFromSaved(template?.builder?.sample))
  const [sampleOpen, setSampleOpen] = useState(false)
  const shownSample = useDeferredValue(sample)
  // On a phone the preview is pinned with the card alone once the builder scrolls. While Sample content is open it
  // is not pinned at all: the panel scrolls away with it instead of growing a pinned preview over the tabs, and
  // stays where the operator left it (hiding it on scroll would take its height away under their finger).
  const pinPreview = !(phone && sampleOpen)
  const sampleId = `${idPrefix}-sample`
  const sampleEdited = !sampleIsDefault(sample)
  const toggleSample = () => {
    // Closing pins the preview again, and a pinned preview scrolled down has no room for this toggle: on a phone
    // the builder goes back to its first screen, where the toggle stays and keeps the focus.
    if (sampleOpen && phone && scrolled) toTop()
    setSampleOpen(!sampleOpen)
  }
  // The sample team as this organization gets it: the one picked under Sample content, else the template's team
  // (#602), else the Eagles, with the real logo when it may show logos. While another team loads the previous one
  // stays on the cards. Errors are swallowed: the preview then keeps the placeholder crest.
  const previewTeamId = sample.team || choices.team || ''
  const previewTeamQuery = useQuery({
    queryKey: ['prepacks', 'template-preview-team', orgSlug, previewTeamId],
    queryFn: ({ signal }) => prepacksApi.fetchTemplatePreviewTeam(orgSlug, previewTeamId ? { signal, team: previewTeamId } : { signal }),
    enabled: Boolean(orgSlug),
    staleTime: TEN_MINUTES,
    retry: false,
    placeholderData: (previous) => previous,
  })
  // A picked team the server could not dress still shows, from its catalog entry (colors, no kit or logo).
  const catalogTeam = (id) => (id ? teamsQuery.data?.find((team) => team.id === id) : undefined)
  const previewTeam = previewTeamQuery.data?.team || (previewTeamQuery.isError ? catalogTeam(previewTeamId) : undefined)
  // The catalog's word on the preview team (nickname and sport), which the server's dressed answer lacks.
  const catalogEntry = catalogTeam(previewTeam?.id)
  // Sample shirt numbers while the template shows them, so the Jerseys "Numbers" option visibly does something.
  // The numbers' fields show with the choices as they are; the cards take the deferred ones.
  // Read from the switches, or from the hand-edited HTML's token once that is what draws the card.
  const numbersShown = builderShowsJerseyNumbers(choices)
  const sampleNumbers = builderShowsJerseyNumbers(deferredChoices)
  const sampleTeam = useMemo(() => (previewTeam && catalogEntry ? { ...previewTeam, nickname: catalogEntry.nickname, sport: catalogEntry.sport } : previewTeam), [previewTeam, catalogEntry])
  // The summary the sample has for this team and leg count, shown in the empty Summary field.
  const defaultSummary = useMemo(() => buildSamplePack(sampleTeam, { legs: sample.legs }).summary, [sampleTeam, sample.legs])
  const samplePack = useMemo(() => buildSamplePack(sampleTeam, { ...shownSample, numbers: sampleNumbers ? shownSample.numbers : null }), [sampleTeam, sampleNumbers, shownSample])
  const [triedPicture, setTriedPicture] = useState(null)
  // Every builder lifetime gets its own server-cleaned preview owner. The
  // session never enters the saved template; it only tracks discarded paints.
  const [previewSession] = useState(() => globalThis.crypto?.randomUUID?.() || '00000000-0000-4000-8000-000000000000')
  const [paintError, setPaintError] = useState('')
  const { painting, paint: paintBanner } = usePaintBanner(orgSlug)
  const paintSubject = `${samplePack.sport || sampleTeam?.sport || DEFAULT_SAMPLE_SPORT}|${sampleTeam?.id || previewTeamId || PREVIEW_TEAM_ID}|${choices.layout || ''}`
  const tryScene = () => {
    const image = normalizeBannerImage(choices.image)
    if (disabled || painting || !image.prompt) return
    const title = bannerTitleOf(choices.title, image.fill)
    setTriedPicture(null)
    setPaintError('')
    void paintBanner({
      prompt: image.prompt,
      fill: image.fill,
      title,
      layout: choices.layout,
      session: previewSession,
      aiAllowed: image.ai_allowed === true,
      sport: samplePack.sport,
      legs: samplePack.legs,
    }, {
      onSuccess: (result) => setTriedPicture({ url: result.url, prompt: image.prompt, fill: image.fill, layout: choices.layout, title, subject: paintSubject }),
      onError: (message) => {
        setTriedPicture(null)
        setPaintError(message)
        toast.error(message)
      },
    })
  }
  // The shirt-number fields are named after the player legs they number.
  const playerNames = useMemo(() => samplePack.legs.slice(0, SAMPLE_PLAYER_NUMBERS.length).map((leg) => leg.selection_name), [samplePack])
  const sampleFeed = useMemo(() => [samplePack, ...TEMPLATE_SAMPLE_FEED.slice(1)], [samplePack])
  const realLogo = Boolean(teamLogoUrl(previewTeam?.logo_url))
  // Without a real logo the cards alone get a placeholder, on each team bet the sample shows (none with two legs,
  // which are both player props); the colors and code tabs read the real template.
  const sampleLegCount = samplePack.legs.length
  const previewCardTemplate = useMemo(() => (realLogo ? previewTemplate : withPreviewLogo(previewTemplate, sampleTeamBetLegs(sampleLegCount))), [previewTemplate, realLogo, sampleLegCount])
  // Said once the answer is in: while it loads the crest shows without a caption. A team the icon service has no
  // logo for (has_logo false: college teams) says so, since turning logos on would not change it.
  const placeholderLogo = previewCardTemplate !== previewTemplate && (previewTeamQuery.isSuccess || previewTeamQuery.isError)
  const placeholderCaption = catalogEntry?.has_logo === false
    ? 'Logo shown as a placeholder: there’s no logo for this team yet'
    : 'Logo shown as a placeholder: this organization can’t show team logos yet'
  const compile = () => {
    const compiled = compileBuilderTemplate(choices, { id, name: name.trim(), description: description.trim(), isDefault: template?.isDefault })
    const saved = savedSample(sample)
    return saved ? { ...compiled, builder: { ...compiled.builder, sample: saved } } : compiled
  }
  // The Layout tab's three cards; each can be folded so the others get the room.
  const [openCards, setOpenCards] = useState({ layout: true, banner: true, font: true })
  const toggleCard = (key) => setOpenCards((current) => ({ ...current, [key]: !current[key] }))
  const paintedPreview = triedPictureFor(triedPicture, deferredChoices, paintSubject)
  const previewBanner = previewBannerValue(deferredChoices, paintedPreview)
  const painted = triedPictureFor(triedPicture, choices, paintSubject)
  const previewVars = useMemo(() => (previewBanner ? { [BANNER_IMAGE_VARIABLE]: previewBanner } : undefined), [previewBanner])
  // The schedules that use this template (#606), for the note on pictures that change once it is saved. A new
  // template has none. GET /prepacks/recipes carries each schedule's rule; the cache is the Schedules page's.
  const recipesQuery = useQuery({
    queryKey: scheduleQueryKeys.list(orgSlug),
    queryFn: async () => (await prepacksApi.listRecipes(orgSlug)) || { recipes: [], strategies: [] },
    enabled: Boolean(orgSlug && template?.id),
  })
  const recipes = recipesQuery.isError ? null : recipesQuery.data?.recipes
  const scheduleChanges = useMemo(() => (recipes ? scheduleBannerChanges(recipes, template, previewTemplate) : null), [recipes, template, previewTemplate])
  // Why the image banner cannot be saved, shown beside the field once Save was tried; cleared as the choices change.
  const [bannerProblem, setBannerProblem] = useState(null)
  const sceneRef = useRef(null)
  const previewPack = useMemo(() => toPublicPrepack({ ...samplePack, template_id: id, template_vars: previewVars }), [id, samplePack, previewVars])
  const current = TABS[tab]
  const goToTab = (next) => {
    setTab(next)
    // Keep focus on the tab that is now open, whichever control asked for it.
    requestAnimationFrame(() => editorRef.current?.querySelector('[role="tab"][aria-selected="true"]')?.focus())
  }

  const save = () => {
    if (disabled || uploadingPicture) return
    if (!name.trim()) {
      setNameMissing(true)
      toast.error('Enter a template name')
      nameRef.current?.focus()
      return
    }
    const problem = bannerProblemOf(choices)
    if (problem) {
      setBannerProblem(problem)
      setOpenCards((current) => ({ ...current, banner: true }))
      goToTab(0)
      requestAnimationFrame(() => sceneRef.current?.focus())
      return
    }
    void onSave(compile())
  }
  // What a described design replaced, so Undo can put it back: the choices, and a name or
  // description it filled in. Individual colors, shell styling and hand-edited code are kept
  // across a design (they are the operator's), and Undo leaves them as they are now.
  const applyDesign = (answer, requested = {}) => {
    // Remember what each filled field held and what it was set to, so Undo only reverts text the operator left as the design wrote it.
    const filled = {
      name: !name.trim() && answer.name ? { was: name, set: answer.name } : undefined,
      description: !description.trim() && answer.description ? { was: description, set: answer.description } : undefined,
    }
    // The server answers the request's team back when the description names none. A team picked in Colors while
    // the request ran must win over that echo, so only a team the model switched to, or dropped, replaces the current
    // one. A team the operator chose under "Meant another team?" is a switch even when it is the snapshot's own team.
    const answered = typeof answer.team?.team_id === 'string' ? answer.team.team_id : ''
    const kept = !requested.pick && answered === (requested.team || '')
    const teamFor = (current) => (kept ? current || '' : answered)
    // What the description does to "Allow AI pictures". Nothing is painted: "on" allows them with the scene as the
    // default one, "off" turns them off, "keep" leaves both. A server from before ai_pictures sends a scene only
    // for "on", so a missing answer reads from the scene.
    const scene = (answer.banner === BANNER_IMAGE || answer.ai_pictures === AI_PICTURES_ON) && typeof answer.image_prompt === 'string' ? clampRunes(answer.image_prompt.replace(WHITESPACE_RUN_PATTERN, ' ').trim()) : ''
    const aiPictures = AI_PICTURES.includes(answer.ai_pictures) ? answer.ai_pictures : (scene ? AI_PICTURES_ON : AI_PICTURES_KEEP)
    const answerBanner = aiPictures === AI_PICTURES_ON ? BANNER_IMAGE : answer.banner
    setChoices((current) => {
      // With hand-edited code the layout, features and options no longer draw anything, and would still
      // restyle the native button and frame, so only the colors and the team (which draws nothing) are taken from the answer.
      if (current.code) {
        const { team: _team, ...rest } = current
        const team = teamFor(current.team)
        return { ...rest, ...(team ? { team } : {}), palette: normalizeBuilderChoices({ palette: answer.palette }).palette }
      }
      // The picture keeps the framing and fixed picture already set. "On" without a scene keeps the current one.
      // "Off" keeps the image banner, so a template without a fixed picture asks for one (bannerProblem below).
      // With "keep" the image banner needs a picture or scene the model cannot supply: the current one is kept
      // when there is one, else the answer gets a gradient instead.
      let image = current.image
      if (aiPictures === AI_PICTURES_ON) {
        image = { ...normalizeBannerImage(current.image), ai_allowed: true, prompt: scene || (typeof current.image?.prompt === 'string' ? current.image.prompt : '') }
      } else if (aiPictures === AI_PICTURES_OFF && answerBanner === BANNER_IMAGE) {
        image = { ...normalizeBannerImage(current.image), ai_allowed: false }
      }
      return normalizeBuilderChoices({ ...current, team: teamFor(current.team), layout: answer.layout, banner: answerBanner === BANNER_IMAGE && !image ? 'gradient' : answerBanner, image, font: answer.font, features: answer.features, options: keepRetiredOptions(current.options, answer.options), palette: answer.palette })
    })
    // AI pictures off without a fixed picture: say so in the Banner card now rather than at Save.
    if (!choices.code && aiPictures === AI_PICTURES_OFF && answerBanner === BANNER_IMAGE && !normalizeBannerImage(choices.image).url) {
      setBannerProblem(true)
      setOpenCards((current) => ({ ...current, banner: true }))
    }
    if (filled.name) setName(answer.name)
    if (filled.description) setDescription(answer.description)
    return filled
  }
  const undoDesign = (before, filled) => {
    setChoices((current) => {
      const { css: _css, ...kept } = before
      return { ...kept, overrides: current.overrides, shell: current.shell, ...(current.css ? { css: current.css } : {}), ...(current.code ? { code: current.code } : {}) }
    })
    if (filled?.name) setName((current) => (current === filled.name.set ? filled.name.was : current))
    if (filled?.description) setDescription((current) => (current === filled.description.set ? filled.description.was : current))
  }
  const nameInvalid = nameMissing && !name.trim()
  const codeLocked = Boolean(choices.code)
  // Hand editing starts from what the choices generate right now, the added CSS included; `css` stays in the
  // choices, inactive, and applies again once the code edits are discarded.
  const editCode = () => {
    if (!codeLocked) {
      const compiled = compileBuilderTemplate(choices, { id })
      setChoices({ ...choices, code: { html: compiled.htmlCode, css: compiled.cssCode, lightCss: compiled.variants.light.cssCode, darkCss: compiled.variants.dark.cssCode } })
    }
    goToTab(ADVANCED_TAB)
  }
  const discardCode = () => {
    const { code: _code, ...rest } = choices
    setChoices(rest)
  }

  return (
    // relative: sr-only headings/legends are absolutely positioned; without a positioned ancestor they anchor to the viewport and stretch the document past the shell (#1066).
    <div ref={rootRef} style={phone ? { height: phoneHeight } : undefined} onScroll={onScroll} className={`relative flex flex-col overflow-y-auto bg-[var(--prepacks-bg)] text-[var(--prepacks-text)] lg:h-full lg:min-h-0 lg:flex-1 lg:overflow-visible ${THIN_SCROLL}`}>
      {/* One row at every width: on a phone it is the back arrow, the name and Save, nothing else. */}
      <header className="sticky top-0 z-20 flex shrink-0 items-center gap-2 border-b border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-3 py-2 sm:gap-3 sm:px-4 sm:py-3">
        <button type="button" onClick={onCancel} className="rounded p-1 text-[var(--prepacks-muted)] hover:text-[var(--prepacks-text)]" aria-label="Back to templates"><ArrowLeft className="h-4 w-4" /></button>
        <h1 className="sr-only text-sm font-semibold sm:not-sr-only">{template ? 'Edit template' : 'Create template'}</h1>
        <input ref={nameRef} aria-label="Template name" name="template-name" autoComplete="off" required aria-invalid={nameInvalid || undefined} value={name} maxLength={80} onChange={(event) => setName(event.target.value)} disabled={disabled} placeholder="Template name"
          className={`${FIELD} h-9 flex-1 text-sm font-semibold placeholder:font-normal placeholder:text-[var(--prepacks-muted)] sm:ml-3 sm:max-w-[340px] ${nameInvalid ? '!border-[var(--wf-color-danger)]' : ''}`} />
        <span className="hidden flex-1 sm:block" />
        <button type="button" onClick={onCancel} className={`${QUIET_BUTTON} hidden sm:block`}>Cancel</button>
        {canConfigure ? <button type="button" onClick={save} disabled={disabled || uploadingPicture} aria-label={isSaving ? 'Saving…' : 'Save template'} className={`${PRIMARY_BUTTON} shrink-0`}>{isSaving ? 'Saving…' : <>Save<span className="hidden sm:inline"> template</span></>}</button> : null}
      </header>
      {/* Phone: the preview sticks to the top while the tabs scroll under it. Desktop: tabs left, preview right. (The describe prompt, when shown, sits under the preview on both.) */}
      <div className="flex flex-col lg:grid lg:min-h-0 lg:flex-1 lg:grid-cols-[440px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)_auto] lg:overflow-hidden">
        {/* Phone, first screen: the card centered with "Choose by hand" at the bottom and nothing else. Once scrolled (or
            it is pressed) it shrinks to the card alone and stays pinned while the tabs scroll up under it. */}
        {/* With Sample content open the first screen takes the height it needs, so the panel never runs over the card and "Choose by hand". */}
        <section ref={previewRef} style={phone && !scrolled && pinPreview ? { height: phoneHeight - PHONE_HEADER_HEIGHT } : undefined} className={`${pinPreview ? 'sticky top-[53px] z-10' : 'relative'} flex w-full shrink-0 flex-col items-center gap-3 bg-[var(--prepacks-bg)] px-3 pb-3 pt-3 lg:static lg:min-h-0 lg:overflow-y-auto lg:px-6 lg:pt-4`}>
          <h2 className="sr-only">Live preview with sample content</h2>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <div className={SEGMENT} role="group" aria-label="Preview as">
              {PREVIEW_VIEWS.map(([view, label]) => (
                <button key={view} type="button" aria-pressed={view === previewView} onClick={() => setPreviewView(view)} className={segmentButton(view === previewView)}>{label}</button>
              ))}
            </div>
            {previewView === 'card' ? (
              <div className={`${SEGMENT} lg:hidden`} role="group" aria-label="Card to preview">
                {BUILDER_MODES.map((mode) => (
                  <button key={mode} type="button" aria-pressed={mode === phoneMode} onClick={() => setPhoneMode(mode)} className={segmentButton(mode === phoneMode)}>{mode === 'light' ? 'Light' : 'Dark'}</button>
                ))}
              </div>
            ) : null}
            {/* A pinned phone preview keeps to one row of controls and the card; Sample content waits until it grows back. */}
            {phone && scrolled && pinPreview ? null : (
              // Open, it looks like a selected segment. The dot says, once closed, that the sample is not the default.
              <button type="button" aria-expanded={sampleOpen} aria-controls={sampleId} aria-label={sampleEdited ? 'Sample content, edited' : undefined} onClick={toggleSample}
                className={`${QUIET_BUTTON} inline-flex h-[38px] shrink-0 items-center gap-1.5 !py-0 ${sampleOpen ? 'bg-[var(--prepacks-raised)] !text-[var(--prepacks-accent-text)]' : ''}`}>
                <PencilLine className="h-3.5 w-3.5" aria-hidden="true" />Sample content
                {sampleEdited ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--prepacks-accent)]" aria-hidden="true" /> : null}
              </button>
            )}
          </div>
          {/* Always there, so the toggle's aria-controls names a real element. Its fields mount only while it is open;
              its team picker is named "Sample team" so it never shares a name with the Colors tab's "Team". */}
          <div id={sampleId} role="group" aria-label="Sample content" hidden={!sampleOpen} className="w-full max-w-[720px] rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-3 text-xs">
            {sampleOpen ? (
              <SampleContentPanel sample={sample} defaultSummary={defaultSummary} templateTeam={choices.team || ''} teamsQuery={teamsQuery} previewTeam={previewTeam}
                numbersShown={numbersShown} playerNames={playerNames} onChange={setSample} />
            ) : null}
          </div>
          <div ref={previewCardsRef} className="flex w-full flex-1 items-center justify-center">
            {previewView === 'feed' ? <FeedPreview template={previewCardTemplate} packs={sampleFeed} templateVars={previewVars} /> : (
              <div className="flex max-w-full items-start justify-center gap-5">
                {BUILDER_MODES.map((mode) => (
                  <div key={mode} className={mode === phoneMode ? '' : 'hidden lg:block'}>
                    <PreviewCard template={previewCardTemplate} pack={previewPack} mode={mode} />
                  </div>
                ))}
              </div>
            )}
          </div>
          {placeholderLogo ? <p className="text-center text-[11px] text-[var(--prepacks-muted)]">{placeholderCaption}</p> : null}
          {phone && canConfigure ? (
            <div className={scrolled ? 'hidden' : 'contents'}>
              {DESCRIBE_CARD_SHOWN ? <DescribeCard orgSlug={orgSlug} choices={choices} disabled={disabled} codeLocked={codeLocked} onApply={applyDesign} onUndo={undoDesign} /> : null}
              {/* The tabs come to rest under a pinned card, so the panel that unpins it closes first. */}
              <button type="button" onClick={() => { setSampleOpen(false); chooseByHand() }} className="flex items-center gap-1 text-xs text-[var(--prepacks-muted)]">
                Choose by hand<ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </section>
        {!phone && DESCRIBE_CARD_SHOWN ? (
          <section className="flex w-full flex-col items-center gap-3 px-6 pb-5 pt-1">
            {canConfigure ? <DescribeCard orgSlug={orgSlug} choices={choices} disabled={disabled} codeLocked={codeLocked} onApply={applyDesign} onUndo={undoDesign} /> : null}
          </section>
        ) : null}
        <section ref={editorRef} className="flex w-full shrink-0 flex-col border-t border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:min-h-0 lg:border-r lg:border-t-0">
          <Tabs idPrefix={idPrefix} active={tab} onChange={goToTab} />
          {current.key === 'layout' || uploadingPicture ? (
            // Three cards. The Layout card takes whatever height the banner and font cards leave and scrolls its tiles inside,
            // so the panel only scrolls when even the cards' minimum heights do not fit; folding a card hands its room to the others.
            // A fixed picture still uploading keeps this panel (hidden) and the Banner card mounted on another tab or once
            // folded, so the upload lands in the template and Save keeps waiting for it.
            <div role="tabpanel" hidden={current.key !== 'layout'} id={current.key === 'layout' ? `${idPrefix}-panel` : undefined} aria-labelledby={`${idPrefix}-tab-layout`} className={`flex flex-col gap-3 p-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto ${THIN_SCROLL}`}>
              {codeLocked ? <CodeLockNote onDiscard={discardCode} disabled={disabled} /> : null}
              <PrepackChoiceCard title="Layout" summary={pickName(BUILDER_LAYOUTS, choices.layout)} open={openCards.layout} onToggle={() => toggleCard('layout')} grow>
                <div className={`-m-0.5 max-h-[400px] min-h-[150px] overflow-y-auto p-0.5 [scrollbar-gutter:stable] lg:max-h-none lg:min-h-0 lg:flex-1 ${THIN_SCROLL}`}>
                  <LayoutStep choices={choices} thumbnailChoices={deferredChoices} mode={branding.darkMode ? 'dark' : 'light'} disabled={disabled || codeLocked} onChange={setChoices} />
                </div>
              </PrepackChoiceCard>
              <PrepackChoiceCard title="Banner" summary={bannerSummary(choices)} open={openCards.banner} keepMounted={uploadingPicture} onToggle={() => toggleCard('banner')}>
                <BannerPicker choices={choices} orgSlug={orgSlug} disabled={disabled || codeLocked} problem={bannerProblem && bannerProblemOf(choices)} sceneRef={sceneRef} scheduleChanges={scheduleChanges} cardAspect={cardAspect} headerAspect={headerAspect} paint={{ painting, painted, previewPainted: paintedPreview, error: paintError, onGenerate: tryScene, onChange: () => setPaintError('') }} onChange={setChoices} onUploadChange={setUploadingPicture} />
              </PrepackChoiceCard>
              <PrepackChoiceCard title="Title font" summary={layoutHasOwnFont(choices.layout) ? 'Oswald (Editorial)' : pickName(BUILDER_FONTS, choices.font)} open={openCards.font} onToggle={() => toggleCard('font')}>
                <FontPicker choices={choices} disabled={disabled || codeLocked} onChange={setChoices} />
              </PrepackChoiceCard>
            </div>
          ) : null}
          {current.key !== 'layout' ? (
            <div role="tabpanel" id={`${idPrefix}-panel`} aria-labelledby={`${idPrefix}-tab-${current.key}`} className={`p-5 lg:min-h-0 lg:flex-1 lg:overflow-y-auto ${THIN_SCROLL}`}>
              <h2 className="sr-only">{current.title}</h2>
              {current.key === 'features' ? (
                <>
                  {codeLocked ? <CodeLockNote onDiscard={discardCode} disabled={disabled} /> : null}
                  <FeaturesStep choices={choices} disabled={disabled || codeLocked} onChange={setChoices} />
                </>
              ) : null}
              {current.key === 'colors' ? <ColorsStep choices={choices} previewTemplate={previewTemplate} teamsQuery={teamsQuery} previewTeam={previewTeam} disabled={disabled} onChange={setChoices} /> : null}
              {current.key === 'advanced' ? <AdvancedStep choices={choices} description={description} previewTemplate={previewTemplate} disabled={disabled} onChange={setChoices} onDescriptionChange={setDescription} onEditCode={editCode} onDiscardCode={discardCode} /> : null}
            </div>
          ) : null}
          <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-[var(--prepacks-border)] px-5 py-3">
            {/* Keyed so a button never turns into a different action under the user's focus. */}
            {tab > 0
              ? <button key="back" type="button" onClick={() => goToTab(tab - 1)} className={QUIET_BUTTON}>Back: {TABS[tab - 1].label}</button>
              : <button key="advanced" type="button" onClick={() => goToTab(ADVANCED_TAB)} className="min-h-6 text-xs text-[var(--prepacks-muted)] underline-offset-2 hover:text-[var(--prepacks-text)] hover:underline">HTML and CSS</button>}
            {tab < LAST_CHOICE_TAB ? <button key="next" type="button" onClick={() => goToTab(tab + 1)} className={QUIET_BUTTON}>Next: {TABS[tab + 1].label}</button> : null}
          </footer>
        </section>
      </div>
    </div>
  )
}
